import { useState, useEffect } from 'react';
import { X, Plus, Building2, Calendar, Clock, MapPin, FileText } from 'lucide-react';
import { createMeeting, validateMeeting } from '../types/Meeting';

// ── Reusable form field (reduces cyclomatic complexity of the main component) ─
const FormField = ({ icon: Icon, label, error, children }) => (
  <div>
    <label style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    }}>
      {Icon && <Icon size={16} style={{ color: '#7c3aed' }} />}
      {label}
    </label>
    {children}
    {error && (
      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{error}</p>
    )}
  </div>
);

// ── Text/date/time input with consistent styling and focus behavior ────────────
const FormInput = ({ type = 'text', value, onChange, error, placeholder, min, max }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    min={min}
    max={max}
    placeholder={placeholder}
    style={{
      width: '100%',
      padding: '12px',
      border: `2px solid ${error ? '#ef4444' : '#e5e7eb'}`,
      borderRadius: '8px',
      fontSize: '16px',
      color: '#1f2937',
      outline: 'none',
      transition: 'border-color 0.2s'
    }}
    onFocus={(e) => { if (!error) e.target.style.borderColor = '#0ea5e9'; }}
    onBlur={(e) => { if (!error) e.target.style.borderColor = '#e5e7eb'; }}
  />
);

// ── Submit button with loading state ──────────────────────────────────────────
const SubmitButton = ({ isSubmitting, isEditing }) => (
  <button
    type="submit"
    disabled={isSubmitting}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: isSubmitting ? '#9ca3af' : '#059669',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: isSubmitting ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => { if (!isSubmitting) e.target.style.backgroundColor = '#047857'; }}
    onMouseLeave={(e) => { if (!isSubmitting) e.target.style.backgroundColor = '#059669'; }}
  >
    {isSubmitting ? (
      <>
        <div style={{
          width: '16px',
          height: '16px',
          border: '2px solid white',
          borderTop: '2px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        Saving...
      </>
    ) : (
      <>
        {isEditing ? <FileText size={16} /> : <Plus size={16} />}
        {isEditing ? 'Save Changes' : 'Add Meeting'}
      </>
    )}
  </button>
);

const AddMeetingForm = ({ trip, onAddMeeting, onCancel, existingMeeting = null, isEditing = false }) => {
  const [meeting, setMeeting] = useState(existingMeeting || createMeeting({}));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (existingMeeting) {
      setMeeting(existingMeeting);
    }
  }, [existingMeeting]);

  const handleInputChange = (field, value) => {
    setMeeting(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validation = validateMeeting(meeting);

    if (!validation.isValid) {
      setErrors(validation.errors);
      setIsSubmitting(false);
      return;
    }

    try {
      await onAddMeeting(meeting);
    } catch (error) {
      console.error('AddMeetingForm: Error saving meeting:', error);
      setErrors({ submit: 'Failed to save meeting. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => {
    if (trip.hotels?.[0]?.checkInDate) {
      return new Date(trip.hotels[0].checkInDate).toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    if (trip.hotels?.[0]?.checkOutDate) {
      return new Date(trip.hotels[0].checkOutDate).toISOString().split('T')[0];
    }
    return '';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Building2 size={20} style={{ color: '#0ea5e9' }} />
            {isEditing ? 'Edit Meeting' : 'Add Business Meeting'}
          </h2>
          <button
            onClick={onCancel}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9ca3af', padding: '4px', borderRadius: '6px',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <FormField icon={Building2} label="Company Name *" error={errors.companyName}>
            <FormInput
              value={meeting.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              error={errors.companyName}
              placeholder="e.g., Acme Corporation"
            />
          </FormField>

          <FormField icon={FileText} label="Meeting Title *" error={errors.meetingTitle}>
            <FormInput
              value={meeting.meetingTitle}
              onChange={(e) => handleInputChange('meetingTitle', e.target.value)}
              error={errors.meetingTitle}
              placeholder="e.g., Q3 Strategy Review"
            />
          </FormField>

          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <FormField icon={Calendar} label="Date *" error={errors.date}>
                <FormInput
                  type="date"
                  value={meeting.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  error={errors.date}
                  min={getMinDate()}
                  max={getMaxDate()}
                />
              </FormField>
            </div>
            <div style={{ flex: 1 }}>
              <FormField icon={Clock} label="Time *" error={errors.time}>
                <FormInput
                  type="time"
                  value={meeting.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                  error={errors.time}
                />
              </FormField>
            </div>
          </div>

          <FormField icon={MapPin} label="Physical Address *" error={errors.address}>
            <FormInput
              value={meeting.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              error={errors.address}
              placeholder="e.g., 123 Main St, City, State, Zip"
            />
          </FormField>

          <FormField label="Notes (Optional)">
            <textarea
              value={meeting.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows="3"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                color: '#1f2937',
                outline: 'none',
                resize: 'vertical',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#0ea5e9'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              placeholder="e.g., Discuss Q4 strategy, bring presentation"
            />
          </FormField>

          {errors.submit && (
            <p style={{ color: '#ef4444', fontSize: '14px', textAlign: 'center' }}>{errors.submit}</p>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '12px 20px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              Cancel
            </button>
            <SubmitButton isSubmitting={isSubmitting} isEditing={isEditing} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMeetingForm;